import { createContext, useContext, useSyncExternalStore } from 'react';
import type { SocketStatus } from './socket';

/** `off`: sem conexão pedida (ninguém conectado). */
export type RealtimeStatus = SocketStatus | 'off';

/**
 * Estado da conexão em tempo real, fora do React: quem conecta (o provedor) escreve, a interface
 * (faixa de aviso) lê. `resume` é o "Reconectar" de quando outra aba assumiu a conexão.
 */
export interface RealtimeStore {
  get: () => RealtimeStatus;
  subscribe: (listener: () => void) => () => void;
  set: (status: RealtimeStatus) => void;
  /** Quem conecta registra aqui o que "reconectar" faz (ou `null` ao parar). */
  onResume: (handler: (() => void) | null) => void;
  resume: () => void;
}

export function createRealtimeStore(): RealtimeStore {
  let status: RealtimeStatus = 'off';
  let resumeHandler: (() => void) | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => status,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    set: (next) => {
      if (next === status) return;
      status = next;
      for (const listener of [...listeners]) listener();
    },
    onResume: (handler) => {
      resumeHandler = handler;
    },
    resume: () => resumeHandler?.(),
  };
}

export const RealtimeStoreContext = createContext<RealtimeStore | null>(null);

function useRealtimeStore(): RealtimeStore {
  const store = useContext(RealtimeStoreContext);
  if (!store) throw new Error('Este componente precisa estar dentro de <RealtimeProvider>.');
  return store;
}

export function useRealtimeStatus(): RealtimeStatus {
  const store = useRealtimeStore();
  return useSyncExternalStore(store.subscribe, store.get);
}

export function useRealtimeResume(): () => void {
  return useRealtimeStore().resume;
}
